import {
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUser,
} from 'amazon-cognito-identity-js';
import _ from 'lodash';
import payloads from '@/payloads/aws/cognito/users';
import { DEFAULT_PROFILE_PIC } from '@/constants';

// the api for cognito we use is located here:
// https://github.com/aws-amplify/amplify-js/tree/master/packages/amazon-cognito-identity-js

// AWS credentials api located here
// https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/Credentials.html
export default {
  _cognitoValidateSession(refresh) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoValidateSession');
    }
    if (this._storm) {
      return true;
    }
    return new Promise(async (resolve) => {
      const session = await this._cognitoRestoreSession();

      if (session) {
        // if there is a session, set aws creds in state so this user can have
        // it's limited permissions
        const Creds = this.$aws.CognitoIdentityCredentials;
        const cogUrl = `cognito-idp.us-west-2.amazonaws.com/${process.env.COGNITO_USERPOOL_ID}`;
        const params = {
          IdentityPoolId: process.env.COGNITO_FEDERATED_ID,
          Logins: {
            [cogUrl]: session.getIdToken().getJwtToken(),
          },
        };
        this.$aws.config.credentials = new Creds(params, { region: process.env.COGNITO_REGION });
        // grab user IdentityId
        delete params.IdentityId;
        const user = await new this.$aws.CognitoIdentity({
          region: process.env.COGNITO_REGION,
        })
          .getId(params)
          .promise();
        // store this in state
        this.$store.dispatch('setUserIdentitiyId', user.IdentityId);
        this.$store.dispatch('setUserSession', { session });
        this.$log.debug('session set for: ', session.idToken.payload.email);

        // this is for state editing purposes. we only set it upon first login and not
        // user refresh sessions
        if (!refresh) {
          await this._cognitoSetUserAttributesState();
        }
        await this.$aws.config.credentials.getPromise();
        this.$log.info('session aws credentials: ', this.$aws.config.credentials);
        resolve(session);
      } else {
        // route to login if there is no user session
        this.$log.error('session failed initial validation');
        this.$router.push({ path: '/' });
        resolve(false);
      }
    });
  },
  _cognitoSetUserAttributesState() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoSetUserAttributesState');
    }
    const attrs = {};
    _.forEach(this.userAttributes, (attr) => {
      if (attr.Value) {
        if (attr.Name === 'sub') {
          attrs.sub = attr.Value;
        }
        if (attr.Name === 'name') {
          attrs.name = attr.Value;
        }
        if (attr.Name === 'email') {
          attrs.email = attr.Value;
        }
        if (attr.Name === 'website') {
          attrs.website = attr.Value;
        }
        if (attr.Name === 'phone_number') {
          attrs.phone = attr.Value;
        }
        if (attr.Name === 'picture') {
          attrs.picture = attr.Value;
        }
        if (attr.Name === 'identities') {
          attrs.identities = attr.Value;
        }
        if (attr.Name === 'custom:stripe_id') {
          attrs.stripeId = attr.Value;
        }
        if (attr.Name === 'custom:account_type') {
          attrs.accountType = attr.Value;
        }
        if (attr.Name === 'custom:sub_user_owner') {
          attrs.sub_user_owner = attr.Value;
        }
        if (attr.Name === 'phone_number_verified') {
          attrs.phone_number_verified = attr.Value;
        }
        if (attr.Name === 'custom:identity_id') {
          attrs.identity_id = attr.Value;
        }
      }
    });
    // pic needs a fallback in case none
    if (!attrs.picture) {
      attrs.picture = DEFAULT_PROFILE_PIC;
    }
    return this.$store.dispatch('setCurrentUser', { values: attrs, vm: this });
  },
  _cognitoRestoreSession() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoRestoreSession');
    }
    if (this._storm) {
      return true;
    }
    const cognitoUser = this.userPool.getCurrentUser();
    return new Promise((resolve, reject) => {
      if (cognitoUser !== null) {
        // there is a saved session
        cognitoUser.getSession(async (err, session) => {
          if (err) {
            this.$log.error('restore session failed: ', err);
            // user was deleted and trying to reload so move them
            // to login
            if (['NotAuthorizedException', 'UserNotFoundException'].includes(err.code)) {
              this.$router.push({ path: '/' });
              if (err.code === 'UserNotFoundException') {
                localStorage.clear();
              }
            }
            reject(err);
          }
          if (session && session.isValid()) {
            // add user attributes to state for editing
            const attrs = await this._cognitoGetUserAttrs(cognitoUser);
            this.$store.dispatch('setUserAttributes', attrs);
            resolve(session);
          } else {
            this.$log.error('session expired');
            resolve(false);
          }
        });
      } else {
        // there is no session
        resolve(false);
      }
    });
  },
  _getCognitoUser() {
    const cognitoUser = this.userPool.getCurrentUser();
    return new Promise((resolve, reject) => {
      if (cognitoUser !== null) {
        cognitoUser.getSession(async (sessionError, session) => {
          if (sessionError) {
            reject(sessionError);
          }
          if (session && session.isValid()) {
            resolve(cognitoUser);
          }
        });
      } else {
        resolve(false);
      }
    });
  },
  _cognitoToggleMFA(params) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoToggleMFA');
    }
    const cognitoUser = this.userPool.getCurrentUser();
    return new Promise((resolve, reject) => {
      if (cognitoUser !== null) {
        cognitoUser.getSession(async (sessionError, session) => {
          if (sessionError) {
            reject(sessionError);
          }
          if (session && session.isValid()) {
            const smsMfaSettings = {
              PreferredMfa: params.enabled,
              Enabled: params.enabled,
            };
            cognitoUser.setUserMfaPreference(smsMfaSettings, null, (mfaError, result) => {
              if (mfaError) {
                reject(mfaError.message || mfaError);
              }
              resolve(result);
            });
          }
        });
      } else {
        resolve(false);
      }
    });
  },
  async _cognitoGetInitialMFAcode() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoGetInitialMFAcode');
    }
    const cognitoUser = await this._getCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.getAttributeVerificationCode('phone_number', {
        onSuccess(result) {
          resolve(result);
        },
        onFailure(err) {
          reject(err);
        },
        // we set to null and not a function because we use another
        // function to verify the phone_number attr with their code
        inputVerificationCode: null,
      });
    });
  },
  async _cognitoVerifyInitialMFA(params) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoVerifyInitialMFA');
    }
    const cognitoUser = await this._getCognitoUser();
    return new Promise((resolve, reject) => {
      cognitoUser.verifyAttribute('phone_number', params.code, {
        onSuccess(result) {
          resolve(result);
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  },
  async _loginSendMFAcode(params) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_loginSendMFAcode');
    }
    const cognitoUser = this._mfaUser;
    return new Promise((resolve, reject) => {
      cognitoUser.sendMFACode(params.code, {
        onSuccess(result) {
          resolve(result);
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  },
  _cognitoLogin(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoLogin');
    }
    const authData = {
      Username: p.username,
      Password: p.password,
    };
    const authDetails = new AuthenticationDetails(authData);

    const userData = {
      Username: p.username,
      Pool: this.userPool,
    };

    const $vm = this;
    const cognitoUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess(result) {
          resolve(result);
        },
        onFailure(err) {
          reject(err);
        },
        mfaSetup(challengeName, challengeParameters) {
          $vm.$log.debug('selectMFAType: ', challengeParameters);
          reject(challengeName);
        },
        associateSecretCode(secretCode) {
          reject(secretCode);
        },
        selectMFAType(challengeName, challengeParameters) {
          $vm.$log.debug('selectMFAType: ', challengeParameters);
          reject(challengeName);
        },
        totpRequired(secretCode) {
          reject(secretCode);
        },
        mfaRequired(codeDeliveryDetails) {
          $vm._mfaUser = cognitoUser;
          reject(codeDeliveryDetails);
        },
      });
    });
  },
  _cognitoLogout() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoLogout');
    }
    const cognitoUser = this.userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      if (cognitoUser) {
        resolve(cognitoUser.signOut());
      } else {
        reject('could not log out user');
      }
    });
  },
  _cognitoGetUserAttrs(user) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoGetUserAttrs');
    }
    const cognitoUser = user || this.userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((err, result) => {
        if (err) {
          reject(err.message || JSON.stringify(err));
          // if we reach here, the user does not exist
          this.$router.push({ path: '/' });
          return;
        }
        resolve(result);
      });
    });
  },
  _cognitoUpdateAttrs(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoUpdateAttrs');
    }
    const cognitoUser = this.userPool.getCurrentUser();

    const attrs = [];
    p.attrs.forEach((attr) => {
      attrs.push(new CognitoUserAttribute(attr));
    });

    return new Promise((resolve, reject) => {
      if (cognitoUser !== null) {
        // there is a saved session
        cognitoUser.getSession(async (err, session) => {
          if (err) {
            reject(err);
          }
          if (session.isValid()) {
            cognitoUser.updateAttributes(attrs, (err, result) => {
              if (err) {
                reject(err);
                return;
              }
              resolve(result);
            });
          }
        });
      } else {
        // there is no session
        reject(false);
      }
    });
  },
  _cognitoChangePassword(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoChangePassword');
    }
    const cognitoUser = this.userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      if (cognitoUser !== null) {
        // there is a saved session
        cognitoUser.getSession(async (err, session) => {
          if (err) {
            reject(err);
          }
          if (session.isValid()) {
            cognitoUser.changePassword(p.oldPassword, p.newPassword, (err, result) => {
              if (err) {
                reject(err);
              }
              resolve(result);
            });
          }
        });
      } else {
        // there is no session
        reject(false);
      }
    });
  },
  _cognitoForgotPassword(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoForgotPassword');
    }
    const userData = {
      Username: p.email,
      Pool: this.userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  },
  _cognitoSubmitResetPass(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoSubmitResetPass');
    }
    const userData = {
      Username: p.email,
      Pool: this.userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(p.code, p.password, {
        onSuccess() {
          resolve(cognitoUser);
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  },
  _cognitoConfirmNewUser(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoConfirmNewUser');
    }
    const userData = {
      Username: p.email,
      Pool: this.userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(p.code, true, (err) => {
        if (err) {
          reject(err);
          return;
        }
        // if we want to authenticate user right after confirmation and
        // give them a session
        const authData = {
          Username: p.email,
          Password: p.password,
        };
        const authDetails = new AuthenticationDetails(authData);
        cognitoUser.authenticateUser(authDetails, {
          onSuccess(result) {
            resolve(result);
          },
          onFailure(error) {
            reject(error);
          },
        });
      });
    });
  },
  async _cognitoCheckSession() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoCheckSession');
    }
    if (this._storm) {
      return true;
    }
    // since sessions expire after an hour
    const $creds = this.$aws.config.credentials;
    if ($creds && $creds.needsRefresh()) {
      this.$log.warn('session needs refresh');
      await this._cognitoValidateSession(true);
    }
    return $creds;
  },
  _timeBuffer(time) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, time);
    });
  },
  _cognitoResendNewUserCode(p) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoResendNewUserCode');
    }
    const userData = {
      Username: p.email,
      Pool: this.userPool,
    };
    const cognitoUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  },
  _cognitoSocialSignIn(provider) {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoSocialSignIn');
    }
    return this.$amplify.Auth.federatedSignIn({ provider });
  },
  _cognitoAmplifyGetUser() {
    if (this._dev && !this._useStaging) {
      return this._cognitoDevResponse('_cognitoAmplifyGetUser');
    }
    return this.$amplify.Auth.currentAuthenticatedUser();
  },
  _cognitoDevResponse(fn) {
    return new Promise((resolve) => {
      resolve(payloads[fn]({ context: this }));
    });
  },
};



// WEBPACK FOOTER //
// ./src/mixins/aws/cognito.js